import { isNullOrUndefined } from '../../../shared';
import { FluidForm } from '../../../shared';
import React, { useEffect, useState } from 'react';
import { DocFluidForm } from './DocFluidForm.component';
import { FormProvider, useFormToast } from './FormContext';
import Loader from '../../components/Loader';
import styled from 'styled-components';
import { deregisterFormInstance, getFormInstance, useFormInstanceDispatch, useFormInstanceListener, useFormInstanceSelect, useRegisterFormInstance } from './FormInstance.service';
import { UnifiedFluidForm } from './unifiedFluidForm/UnifiedFluidForm.component';
import { FormNotFound } from './FormNotFound.component';
import { PageLoader } from '../../components/PageLoader';
import { ErrorComponent } from '../../components/ErrorComponent';
const FormContainer = styled.div.attrs({
    className: 'fluid-form-container @container/fluid-form-container @fluid-form-container'
}).withConfig({
    shouldForwardProp: (prop) => !['headless'].includes(prop),
}) `
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  ${props => props.headless ? `
    height: 100%;
    margin: 0;
    padding: 0;
    border: none;
    box-shadow: none;
    background: transparent;
  ` : ''};
`;
const FormContent = styled.div.attrs({
    className: 'fluid-form-content'
}).withConfig({
    shouldForwardProp: (prop) => !['headless', 'withBranding'].includes(prop),
}) `
  flex: 1;
  position: relative;
  ${props => props.headless ? `
    height: 100%;
    margin: 0;
    padding: 0;
    border: none;
    box-shadow: none;
    background: transparent;
  ` : ''};
  ${props => (props.withBranding) ? `
    margin-top: 1.5rem;
  ` : ''};
`;
const BrandingSection = styled.div.attrs({
    className: 'fluid-branding-section'
}) `
    position: absolute;
    top: -1.2rem;
    right: 3.7rem;
    font-size: 0.8rem;
    color: #9ca3af;
    display: flex;
    align-items: center;
    gap: 0.25rem;
    z-index: 10;
`;
const BrandName = styled.span.attrs({
    className: 'fluid-brand-name'
}) `
  font-weight: 600;
  color: #4b5563;
`;
export function SimpleFormProvider({ config, formKey, onSubmissionCreated, initialData, onSubmit, onChange, onMessageReceived, signature, ...props }) {
    let instanceId = useRegisterFormInstance({ instanceId: props.formInstance, form: props.form, signature });
    let dispatch = useFormInstanceDispatch(instanceId);
    let submissionId = useFormInstanceSelect((state) => state.submissionId, instanceId);
    let formInitialized = useFormInstanceSelect((state) => !!state.fluidForm, instanceId);
    const [formError, setFormError] = useState(null);
    useFormInstanceListener(instanceId, (event) => {
        if (event.type === "chatbotService.messageReceived") {
            let message = event.value[0];
            onMessageReceived?.(message);
        }
    });
    useEffect(() => {
        if (!instanceId) {
            return;
        }
        if (getFormInstance(instanceId)?.getFluidForm()) {
            dispatch("initializeSubmission", props.submissionId, props.autosave);
            return;
        }
        if (formKey) {
            dispatch("loadForm", formKey, { submissionId: props.submissionId, autoSave: props.autosave });
        }
        else if (config) {
            dispatch("setForm", new FluidForm(config), { submissionId: props.submissionId, autoSave: props.autosave });
        }
        else if (props.form) {
            dispatch("setForm", props.form, { submissionId: props.submissionId, autoSave: props.autosave });
        }
        return () => {
            deregisterFormInstance(instanceId);
        };
    }, [instanceId]);
    useFormInstanceListener(instanceId, (event) => {
        if (event.type == "setSubmissionId") {
            onSubmissionCreated?.(event.value);
        }
        else if (event.type === "setFormErrorMessage") {
            const errorMessage = event.value?.[0];
            if (errorMessage) {
                setFormError(errorMessage);
            }
        }
    });
    let waitForSubmissionId = (props.autosave && !submissionId);
    if (formError === "Form not found") {
        return React.createElement(FormNotFound, null);
    }
    if (formError) {
        return React.createElement(ErrorComponent, { iconVariant: "error", title: "Oops! Something is not right", subtitle: formError, ctaButtonProps: {
                text: "Refresh the page",
                href: window.location.href,
            } });
    }
    if (!formInitialized || waitForSubmissionId) {
        let loadingMesage = !formInitialized ? `Loading form ${formKey}` : "Loading Submission";
        return React.createElement(PageLoader, { text: loadingMesage });
    }
    return React.createElement(React.Fragment, null,
        React.createElement(SimpleFormProviderComponent, { ...props, formInstance: instanceId }));
}
export function SimpleFormProviderComponent({ config, formInstance, initialData, onSubmit, initialMessage, render, formProps, view = "form", headless = false, ...props }) {
    let isSubmitting = useFormInstanceSelect((state) => state.isSubmitting, formInstance);
    let validationResults = useFormInstanceSelect((state) => state.fluidForm.getValidationResults(), formInstance);
    let showBranding = useFormInstanceSelect((state) => state.fluidForm?.getAdditionalConfigValue("styleConfig", "showBranding"), formInstance);
    let formStyle = useFormInstanceSelect((state) => state.fluidForm?.getAdditionalConfigValue("styleConfig", "style") || "classic", formInstance);
    let layout = useFormInstanceSelect((state) => state.fluidForm?.getAdditionalConfigValue("styleConfig", "layout") || "form", formInstance);
    let dispatch = useFormInstanceDispatch(formInstance);
    const handleSubmit = async () => {
        dispatch("submit");
    };
    const handleClearSubmission = () => {
        dispatch("clearForm");
    };
    if (isNullOrUndefined(showBranding)) {
        showBranding = true;
    }
    if (formStyle == "fluid" && layout == "form") {
        render = (formProps, onSubmit, validationResults) => {
            return React.createElement(UnifiedFluidForm, { ...formProps, onSubmit: onSubmit, validationResults: validationResults, headless: headless });
        };
    }
    if (getFormInstance(formInstance).getFluidForm().getFormConfig().fields.length == 0) {
        return React.createElement("div", { className: "text-center text-gray-500 py-4" }, "No fields in this form");
    }
    return (React.createElement(FormProvider, { formInstance: formInstance },
        React.createElement(FormContainer, { headless: headless },
            isSubmitting && (React.createElement(Loader, { text: "Submitting your form..." })),
            React.createElement(FormContent, { headless: headless, withBranding: showBranding },
                showBranding && (React.createElement(BrandingSection, null,
                    React.createElement("span", null, "Powered by"),
                    React.createElement(BrandName, null, "FluidForm"))),
                props.children,
                render && render(formProps, (data) => handleSubmit(), validationResults),
                !render && !props.children && React.createElement(DocFluidForm, { ...formProps, headless: headless, validationResults: validationResults, onSubmit: (data) => handleSubmit() }),
                React.createElement(FormActionErrorRenderer, { formInstance: formInstance })))));
}
function FormActionErrorRenderer({ formInstance }) {
    const { addToast } = useFormToast();
    useFormInstanceListener(formInstance, (event) => {
        if (event.type === "setActionErrorMessage") {
            const errorMessage = event.value?.[0];
            if (errorMessage) {
                addToast(errorMessage, 'error', undefined);
            }
        }
    });
    return null;
}
export { parseError } from './errorUtils';
// Nice powered by branding
{ /* <div
className="inline-flex items-center gap-2 px-4 py-2 rounded-full mt-8 text-sm font-medium"
style={{
  fontFamily: 'var(--font-family)',
  backgroundColor: 'rgba(251, 175, 77, 0.1)',
  color: 'var(--primary-color)',
}}
>
<span>Powered by</span>
<span style={{ color: 'var(--secondary-color)', fontWeight: 'var(--font-weight-semibold)' }}>FluidForm</span>
</div> */
}
